import React from "react";
import { Package, Hash, FileText, Tag, Layers } from "lucide-react";
import "./ProductDetailsModal.scss";
import IconButton from "../IconButton/IconButton";

const ProductDetailsModal = ({ isOpen, onClose, productHistory }) => {
  if (!isOpen) return null;

  // Helper function to safely get nested values
  const getProductName = () => {
    return (
      productHistory?.productName ||
      productHistory?.product?.name ||
      "Unknown Product"
    );
  };

  const getProductCode = () => {
    return productHistory?.product?.code || productHistory?.skuCode || null;
  };

  const getSpecifications = () => {
    // Check for specificationHistories array
    if (productHistory?.specificationHistories?.length > 0) {
      return productHistory.specificationHistories.map((spec) => ({
        id: spec.id,
        name:
          spec.specsName ||
          spec.productSpecification?.specName ||
          "Specification",
        value: spec.valueName || spec.productSpecificationValue?.value || "N/A",
        unit: spec.productSpecificationValue?.unit || spec.unit || null,
      }));
    }
    return [];
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const specifications = getSpecifications();
  const quantity = productHistory?.quantity || 0;
  const price = productHistory?.price || 0;
  const total = price * quantity;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content product-details-modal enhanced"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-content">
            <Package className="header-icon" size={24} />
            <h2>Product Details</h2>
          </div>
          <IconButton
            icon="mdi:close"
            text=""
            onClick={onClose}
            className="close-btn"
            title="Close"
          />
        </div>

        <div className="modal-body">
          {productHistory ? (
            <div className="product-details-content">
              {/* Product Header Section */}
              <div className="product-header-section">
                <div className="product-title-row">
                  <h3 className="product-name">{getProductName()}</h3>
                  {getProductCode() && (
                    <span className="product-code-badge">
                      <Hash size={14} />
                      {getProductCode()}
                    </span>
                  )}
                </div>
              </div>

              {/* Price & Quantity Summary Card */}
              <div className="summary-card">
                <div className="summary-item">
                  <label>Quantity</label>
                  <div className="summary-value">
                    <Layers size={18} />
                    <span className="value-text">{quantity}</span>
                  </div>
                </div>

                <div className="summary-divider"></div>

                <div className="summary-item">
                  <label>Unit Price</label>
                  <div className="summary-value">
                    <span className="value-text">₹{formatCurrency(price)}</span>
                  </div>
                </div>

                <div className="summary-divider"></div>

                <div className="summary-item total">
                  <label>Total Amount</label>
                  <div className="summary-value">
                    <span className="value-text total-amount">
                      ₹{formatCurrency(total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Product Description */}
              {productHistory?.product?.description && (
                <div className="detail-section">
                  <div className="section-header">
                    <FileText size={18} />
                    <label>Description</label>
                  </div>
                  <p className="description-text">
                    {productHistory.product.description}
                  </p>
                </div>
              )}

              {/* Product Type (if available) */}
              {productHistory?.product?.productType && (
                <div className="detail-section">
                  <div className="section-header">
                    <Tag size={18} />
                    <label>Product Type</label>
                  </div>
                  <span
                    className={`type-badge type-${productHistory.product.productType.toLowerCase()}`}
                  >
                    {productHistory.product.productType}
                  </span>
                </div>
              )}

              {/* Specifications Section */}
              {specifications.length > 0 && (
                <div className="detail-section specifications-section">
                  <div className="section-header">
                    <Layers size={18} />
                    <label>Product Specifications</label>
                    <span className="spec-count">{specifications.length}</span>
                  </div>
                  <div className="specifications-grid">
                    {specifications.map((spec, idx) => (
                      <div key={spec.id || idx} className="specification-card">
                        <div className="spec-name">{spec.name}</div>
                        <div className="spec-value">
                          {spec.value}
                          {spec.unit && (
                            <span className="spec-unit"> {spec.unit}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Information */}
              {(productHistory?.remarks || productHistory?.notes) && (
                <div className="detail-section">
                  <div className="section-header">
                    <FileText size={18} />
                    <label>Additional Notes</label>
                  </div>
                  <div className="notes-box">
                    <p>{productHistory.remarks || productHistory.notes}</p>
                  </div>
                </div>
              )}

              {/* Status Information (if available) */}
              {productHistory?.status && (
                <div className="detail-section">
                  <div className="section-header">
                    <Tag size={18} />
                    <label>Status</label>
                  </div>
                  <span
                    className={`status-badge status-${productHistory.status.toLowerCase()}`}
                  >
                    {productHistory.status}
                  </span>
                </div>
              )}

              {/* Timestamps (if available) */}
              {(productHistory?.createdAt || productHistory?.updatedAt) && (
                <div className="timestamps-section">
                  {productHistory.createdAt && (
                    <div className="timestamp-item">
                      <label>Created:</label>
                      <span>
                        {new Date(productHistory.createdAt).toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                  )}
                  {productHistory.updatedAt && (
                    <div className="timestamp-item">
                      <label>Updated:</label>
                      <span>
                        {new Date(productHistory.updatedAt).toLocaleString(
                          "en-IN"
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="no-data">
              <Package size={48} className="no-data-icon" />
              <p>No product details available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailsModal;
